export type ScoreCodeLanguage = "PYTHON" | "JAVASCRIPT";

export interface CustomCodeExample {
  id: string;
  label: string;
  description: string;
  language: ScoreCodeLanguage;
  code: string;
  responseShape:
    | "score"
    | "score+sub_scores"
    | "score+score_matrix_2d"
    | "score+score_matrix_2d_objects"
    | "score+score_matrix_3d_single_table";
}

/**
 * Backend custom scoring contract (smart tables):
 * - required: `score` (number)
 * - optional: `score_matrix`
 *   - 2D: list[list[cell]]
 *   - OR single-table 3D: list[list[list[cell]]] where top-level length must be 1
 * - optional: extra keys (e.g. `sub_scores`) are tolerated and forward-compatible
 *
 * Matrix cell shape:
 * - number | string | null
 * - OR { value: number | string; positive_metric?: boolean }
 */
export const CUSTOM_CODE_RESPONSE_CONTRACT = {
  required: ["score"] as const,
  optional: ["score_matrix", "sub_scores"] as const,
};

export const CUSTOM_CODE_EXAMPLES: CustomCodeExample[] = [
  {
    id: "py-score-only",
    label: "Python: score only (minimum valid response)",
    description: "Returns only the required numeric score.",
    language: "PYTHON",
    responseShape: "score",
    code: `# data: list[dict[str, Any]]
# Return shape: {"score": number}

def score(data):
    if not data:
        return {"score": 0.0}

    # Example: percent of rows with any non-empty value
    non_empty_rows = 0
    for row in data:
        has_value = any(v is not None and str(v).strip() != "" for v in row.values())
        if has_value:
            non_empty_rows += 1

    return {"score": non_empty_rows / len(data)}

return score(data)`,
  },
  {
    id: "py-score-with-sub-scores",
    label: "Python: score + sub_scores",
    description: "Adds named sub-scores for richer debugging/UX.",
    language: "PYTHON",
    responseShape: "score+sub_scores",
    code: `# data: list[dict[str, Any]]
# Return shape: {"score": number, "sub_scores": {...}}

def score(data):
    if not data:
        return {"score": 0.0, "sub_scores": {"coverage": 0.0, "quality": 0.0}}

    total = len(data)
    covered = 0
    quality_hits = 0

    for row in data:
        answer = str(row.get("Answer", "")).strip()
        confidence = row.get("Confidence")

        if answer:
            covered += 1

        try:
            confidence_value = float(confidence) if confidence is not None else 0.0
        except Exception:
            confidence_value = 0.0

        if answer and confidence_value >= 0.7:
            quality_hits += 1

    coverage = covered / total
    quality = quality_hits / total
    overall = (coverage * 0.4) + (quality * 0.6)

    return {
        "score": overall,
        "sub_scores": {
            "coverage": coverage,
            "quality": quality,
        },
    }

return score(data)`,
  },
  {
    id: "py-score-with-matrix-2d",
    label: "Python: score + 2D score_matrix (primitive cells)",
    description: "Returns a 2D matrix with numeric and string cells.",
    language: "PYTHON",
    responseShape: "score+score_matrix_2d",
    code: `# data: list[dict[str, Any]]
# Return shape: {"score": number, "score_matrix": list[list[number|string|null]]}

def score(data):
    if not data:
        return {"score": 0.0, "score_matrix": []}

    matrix = []
    total = 0.0

    # Columns: [row quality (0..1), status]
    for row in data:
        text = str(row.get("Answer", "")).strip()
        quality = 1.0 if len(text) >= 20 else 0.5 if text else 0.0
        status = "good" if quality >= 0.8 else "needs_review"
        matrix.append([quality, status])
        total += quality

    return {
        "score": total / len(data),
        "score_matrix": matrix,
    }

return score(data)`,
  },
  {
    id: "py-score-with-matrix-2d-objects",
    label: "Python: score + 2D score_matrix (object cells)",
    description: "Uses semantic matrix cells with value + positive_metric.",
    language: "PYTHON",
    responseShape: "score+score_matrix_2d_objects",
    code: `# data: list[dict[str, Any]]
# Return shape:
# {
#   "score": number,
#   "score_matrix": list[list[number|string|None|{"value": number|string, "positive_metric"?: bool}]]
# }

def score(data):
    if not data:
        return {"score": 0.0, "score_matrix": []}

    matrix = []
    scores = []

    # Columns:
    # 1) Accuracy metric cell object
    # 2) Latency metric cell object (negative metric)
    # 3) Optional note
    for row in data:
        accuracy_raw = row.get("Accuracy", 0)
        latency_raw = row.get("LatencyMs", 0)

        try:
            accuracy = float(accuracy_raw)
        except Exception:
            accuracy = 0.0

        try:
            latency = float(latency_raw)
        except Exception:
            latency = 0.0

        normalized_latency_score = max(0.0, min(1.0, 1.0 - (latency / 5000.0)))
        final_row_score = (accuracy * 0.7) + (normalized_latency_score * 0.3)
        scores.append(final_row_score)

        note = None if final_row_score >= 0.7 else "slow or low quality"

        matrix.append([
            {"value": round(accuracy, 4), "positive_metric": True},
            {"value": round(latency, 2), "positive_metric": False},
            note,
        ])

    return {
        "score": sum(scores) / len(scores),
        "score_matrix": matrix,
    }

return score(data)`,
  },
  {
    id: "py-score-with-matrix-3d-single-table",
    label: "Python: score + single-table 3D score_matrix",
    description: "3D format is accepted only with a single top-level table.",
    language: "PYTHON",
    responseShape: "score+score_matrix_3d_single_table",
    code: `# data: list[dict[str, Any]]
# Return shape: {"score": number, "score_matrix": [<2D matrix>]}
# NOTE: Top-level array length must be 1 for smart tables.

def score(data):
    if not data:
        return {"score": 0.0, "score_matrix": [[]]}

    base_2d = []
    total = 0.0

    for row in data:
        verdict = str(row.get("Verdict", "")).strip().lower()
        row_score = 1.0 if verdict in {"pass", "true", "yes"} else 0.0
        total += row_score
        base_2d.append([row_score, verdict or "unknown"])

    return {
        "score": total / len(data),
        "score_matrix": [base_2d],  # single-table 3D wrapper
    }

return score(data)`,
  },
  {
    id: "js-score-only",
    label: "JavaScript: score only (minimum valid response)",
    description: "Returns only the required numeric score.",
    language: "JAVASCRIPT",
    responseShape: "score",
    code: `// data: Array<Record<string, unknown>>
// Return shape: { score: number }

if (!Array.isArray(data) || data.length === 0) {
  return { score: 0.0 };
}

let nonEmptyRows = 0;

for (const row of data) {
  const hasValue = Object.values(row).some(
    value => value !== null && String(value).trim() !== "",
  );
  if (hasValue) nonEmptyRows += 1;
}

return { score: nonEmptyRows / data.length };`,
  },
  {
    id: "js-score-with-sub-scores",
    label: "JavaScript: score + sub_scores",
    description: "Adds named sub-scores for richer debugging/UX.",
    language: "JAVASCRIPT",
    responseShape: "score+sub_scores",
    code: `// data: Array<Record<string, unknown>>
// Return shape: { score: number, sub_scores: Record<string, number> }

if (!Array.isArray(data) || data.length === 0) {
  return { score: 0.0, sub_scores: { coverage: 0.0, quality: 0.0 } };
}

let covered = 0;
let qualityHits = 0;

for (const row of data) {
  const answer = String(row.Answer ?? "").trim();
  const confidence = Number(row.Confidence ?? 0);

  if (answer) covered += 1;
  if (answer && Number.isFinite(confidence) && confidence >= 0.7) qualityHits += 1;
}

const coverage = covered / data.length;
const quality = qualityHits / data.length;
const score = (coverage * 0.4) + (quality * 0.6);

return {
  score,
  sub_scores: {
    coverage,
    quality,
  },
};`,
  },
  {
    id: "js-score-with-matrix-2d",
    label: "JavaScript: score + 2D score_matrix (primitive cells)",
    description: "Returns a 2D matrix with numeric and string cells.",
    language: "JAVASCRIPT",
    responseShape: "score+score_matrix_2d",
    code: `// data: Array<Record<string, unknown>>
// Return shape: { score: number, score_matrix: Array<Array<number|string|null>> }

if (!Array.isArray(data) || data.length === 0) {
  return { score: 0.0, score_matrix: [] };
}

const matrix = [];
let total = 0;

for (const row of data) {
  const text = String(row.Answer ?? "").trim();
  const quality = text.length >= 20 ? 1.0 : text ? 0.5 : 0.0;
  const status = quality >= 0.8 ? "good" : "needs_review";
  matrix.push([quality, status]);
  total += quality;
}

return {
  score: total / data.length,
  score_matrix: matrix,
};`,
  },
  {
    id: "js-score-with-matrix-2d-objects",
    label: "JavaScript: score + 2D score_matrix (object cells)",
    description: "Uses semantic matrix cells with value + positive_metric.",
    language: "JAVASCRIPT",
    responseShape: "score+score_matrix_2d_objects",
    code: `// data: Array<Record<string, unknown>>
// Return shape:
// {
//   score: number,
//   score_matrix: Array<Array<number|string|null|{ value: number|string; positive_metric?: boolean }>>
// }

if (!Array.isArray(data) || data.length === 0) {
  return { score: 0.0, score_matrix: [] };
}

const matrix = [];
const scores = [];

for (const row of data) {
  const accuracy = Number(row.Accuracy ?? 0);
  const latency = Number(row.LatencyMs ?? 0);

  const safeAccuracy = Number.isFinite(accuracy) ? accuracy : 0;
  const safeLatency = Number.isFinite(latency) ? latency : 0;

  const latencyScore = Math.max(0, Math.min(1, 1 - safeLatency / 5000));
  const finalRowScore = (safeAccuracy * 0.7) + (latencyScore * 0.3);
  scores.push(finalRowScore);

  const note = finalRowScore >= 0.7 ? null : "slow or low quality";

  matrix.push([
    { value: Number(safeAccuracy.toFixed(4)), positive_metric: true },
    { value: Number(safeLatency.toFixed(2)), positive_metric: false },
    note,
  ]);
}

const average = scores.reduce((acc, value) => acc + value, 0) / scores.length;

return {
  score: average,
  score_matrix: matrix,
};`,
  },
  {
    id: "js-score-with-matrix-3d-single-table",
    label: "JavaScript: score + single-table 3D score_matrix",
    description: "3D format is accepted only with a single top-level table.",
    language: "JAVASCRIPT",
    responseShape: "score+score_matrix_3d_single_table",
    code: `// data: Array<Record<string, unknown>>
// Return shape: { score: number, score_matrix: [<2D matrix>] }
// NOTE: Top-level array length must be 1 for smart tables.

if (!Array.isArray(data) || data.length === 0) {
  return { score: 0.0, score_matrix: [[]] };
}

const matrix2d = [];
let total = 0;

for (const row of data) {
  const verdict = String(row.Verdict ?? "").trim().toLowerCase();
  const rowScore = ["pass", "true", "yes"].includes(verdict) ? 1.0 : 0.0;
  total += rowScore;
  matrix2d.push([rowScore, verdict || "unknown"]);
}

return {
  score: total / data.length,
  score_matrix: [matrix2d], // single-table 3D wrapper
};`,
  },
];

export const CUSTOM_CODE_EXAMPLES_BY_LANGUAGE: Record<ScoreCodeLanguage, CustomCodeExample[]> = {
  PYTHON: CUSTOM_CODE_EXAMPLES.filter(example => example.language === "PYTHON"),
  JAVASCRIPT: CUSTOM_CODE_EXAMPLES.filter(example => example.language === "JAVASCRIPT"),
};

export const getCustomCodeExamples = (language: ScoreCodeLanguage): CustomCodeExample[] =>
  CUSTOM_CODE_EXAMPLES_BY_LANGUAGE[language];
